import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ChartFacade } from '@mode/charts/contract';
import { ReportChartViewerFacade } from '@mode/report-viewer/domain';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mode-viz',
  templateUrl: './viz.component.html',
  styleUrls: ['./viz.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VizComponent implements OnInit, OnDestroy, OnChanges {
  @Output() chartLoaded = new EventEmitter<{ rendered: boolean; token: string }>();
  @Input() token = '';
  @Input() title = '';
  @Input() queryToken$!: Observable<string>;
  @Input() height = '';

  @Input() chartType = '';
  @Input() encoding!: any;
  @Input() format!: any;

  @Input() fieldFormats!: any;
  @Input() fieldColors!: any;
  @Input() reportLevelColorMap!: any;
  @Input() facetSizes!: any;
  @Input() reportApiService!: any;

  isVegasRender = false;
  chartIsValid = false;
  planId = '';
  renderModel$?: Observable<any>;
  modeModel$: Observable<FlamingoTypes.ModeModel> | undefined;
  isValid$!: Observable<boolean>;
  isComplete$!: Observable<boolean>;
  canRender$!: Observable<boolean>;
  runState$!: Observable<any>;
  protected destroyed$ = new Subject<void>();

  constructor(
    private reportChartViewerFacade: ReportChartViewerFacade,
    private chartFacade: ChartFacade,
    private flamingoFacade: FlamingoFacade,
    private dataViewFacade: DataViewFacade,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isValid$ = this.chartFacade.createIsValidFromVizToken(this.token);
    this.isComplete$ = this.chartFacade.createIsCompleteFromVizToken(this.token);
    this.canRender$ = combineLatest([this.isValid$, this.isComplete$]).pipe(
      map(([isValid, isComplete]) => isValid && isComplete)
    );
    this.runState$ = this.queryToken$.pipe(
      switchMap((queryToken) => this.dataViewFacade.createQueryRunStateByQueryTokenNoEmpty(queryToken))
    );
    this.modeModel$ = this.dataViewFacade.model$;
    // This is a special case where a component subscribes and dispatches events. This
    // allows us to let the layout and rendering dictate what requests to flamingo get made.
    // Additionally the component lifecycle handles the registering/deregistering of subscriptions
    // rather than having a central management potentially leak subscription handlers.
    this.reportChartViewerFacade
      .createReportViewChartPlan$(this.token)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((chartPlan) => {
        this.planId = chartPlan.plan.id;
        this.reportChartViewerFacade.executeVizPlanFromLayout(chartPlan);
        this.renderModel$ = this.queryToken$?.pipe(
          switchMap((queryToken) =>
            this.reportChartViewerFacade.createVizDataModel$(this.chartType, this.token, queryToken, chartPlan.plan.id)
          )
        );
        this.cdr.markForCheck();
      });

    combineLatest([
      this.canRender$,
      this.runState$,
      this.flamingoFacade.createLoadingByPlanId(this.planId),
      this.flamingoFacade.createInitByPlanId(this.planId),
    ])
      .pipe(
        takeUntil(this.destroyed$),
        filter(([isValid, state, loading, init]) => {
          return isValid && state === 'succeeded' && !loading && !init;
        })
      )
      .subscribe(([isValid]) => {
        this.chartIsValid = isValid;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.chartType) {
      this.isVegasRender = ChartTypes.canMuzeRenderChart(changes.chartType.currentValue);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  renderEnd() {
    if (this.chartIsValid) {
      this.chartLoaded.emit({ rendered: this.chartIsValid, token: this.token });
    }
  }

  renderStart() {}
  dispatchAction() {}
}
